import type { GetProductQuery, ProductCardFragment } from '$gql/storefront/operations';
import type { TranslationKey } from '$lib/i18n/types';
import { constant } from '$lib/storyblok-constants';

type Product = NonNullable<GetProductQuery['product']>;
type Variant =
  | Product['variants']['nodes'][number]
  | ProductCardFragment['variants']['nodes'][number];
type ExtraInfo = { isPreSale: boolean; wontBeRestocked: boolean; wishlistCounter?: string };

const PRODUCT_ALERT_IDS: Record<string, TranslationKey> = {
  PRE_SALE: 'pdp.alerts.product.pre-sale',
  NOT_AVAILABLE: 'pdp.alerts.product.not-available',
  LAST_UNITS: 'pdp.alerts.product.last-units',
  LOW_STOCK: 'pdp.alerts.product.low-stock',
  LOW_STOCK_WISHLIST: 'pdp.alerts.product.low-stock-wishlist',
  LOW_STOCK_COUNTER: 'pdp.alerts.product.low-stock.counter',
  SOME_ITEMS_NOT_AVAILABLE: 'pdp.alerts.product.some-items-not-available'
};

export type ProductAlertType = (typeof PRODUCT_ALERT_IDS)[keyof typeof PRODUCT_ALERT_IDS];

export const getProductAlertId = (product: Product, info: ExtraInfo) => {
  // This product is marked as "pre-sale" (storyblok)
  if (info.isPreSale) return PRODUCT_ALERT_IDS.PRE_SALE;

  // This product is marked as "not available for sale" (shopify)
  if (!product.availableForSale) return PRODUCT_ALERT_IDS.NOT_AVAILABLE;

  const variantMessages = product.variants.nodes?.map((variant) =>
    getVariantAlertId(variant, info)
  );
  const threshold = Number(constant('low_stock_threshold'));

  // Some variants are low on stock and this product won't be restocked
  if (variantMessages?.includes(VARIANT_ALERT_IDS.LOW_STOCK_WONT_RESTOCK)) {
    return PRODUCT_ALERT_IDS.LAST_UNITS;
  }

  // Some variants are low on stock and users have added this product to their wishlist
  if (
    variantMessages?.includes(VARIANT_ALERT_IDS.LOW_STOCK) &&
    info.wishlistCounter &&
    info.wishlistCounter !== '0' &&
    product.totalInventory &&
    product.totalInventory < threshold
  ) {
    return PRODUCT_ALERT_IDS.LOW_STOCK_COUNTER;
  }

  // Some variants are low on stock
  if (variantMessages?.includes(VARIANT_ALERT_IDS.LOW_STOCK)) {
    if (info.wishlistCounter && info.wishlistCounter !== '0')
      return PRODUCT_ALERT_IDS.LOW_STOCK_WISHLIST;
    return PRODUCT_ALERT_IDS.LOW_STOCK;
  }

  // Some variants are out of stock
  if (variantMessages?.includes(VARIANT_ALERT_IDS.NOT_AVAILABLE)) {
    return PRODUCT_ALERT_IDS.SOME_ITEMS_NOT_AVAILABLE;
  }
};

const VARIANT_ALERT_IDS: Record<string, TranslationKey> = {
  NOT_AVAILABLE: 'pdp.alerts.variant.not-available',
  LOW_STOCK: 'pdp.alerts.variant.low-stock',
  LOW_STOCK_WONT_RESTOCK: 'pdp.alerts.variant.low-stock-wont-restock'
};

export const getVariantAlertId = (variant: Variant, { isPreSale, wontBeRestocked }: ExtraInfo) => {
  // This variant is marked as "pre-sale" (storyblok)
  if (isPreSale) return;

  // This variant is marked as "not available for sale" (shopify)
  if (!variant.availableForSale) {
    return VARIANT_ALERT_IDS.NOT_AVAILABLE;
  }

  const availableQty = variant.quantityAvailable || 0;

  if (wontBeRestocked) {
    const threshold = Number(constant('low_stock_threshold_will_not_be_restocked'));

    // This variant is low on stock and won't be restocked
    if (availableQty <= threshold) {
      return VARIANT_ALERT_IDS.LOW_STOCK_WONT_RESTOCK;
    }
  } else {
    const threshold = Number(constant('low_stock_threshold'));

    // This variant is low on stock
    if (availableQty <= threshold) {
      return VARIANT_ALERT_IDS.LOW_STOCK;
    }
  }
};
